// ./src/hooks/useLookupDictionaryItems.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  LookupDictionaryItem,
  LookupDictionaryItemService,
  LookupDictionaryItemFilter,
  LookupDictionaryItemInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseLookupDictionaryItemsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<LookupDictionaryItemFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;
}

export interface UseLookupDictionaryItemsReturn {
  // Data state
  lookupDictionaryItem: LookupDictionaryItem | null;
  lookupDictionaryItems: LookupDictionaryItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<LookupDictionaryItemFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedLookupDictionaryItem: LookupDictionaryItem | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<LookupDictionaryItemFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    shouldReset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<LookupDictionaryItem[]>;
  create: (
    params: LookupDictionaryItemInput
  ) => Promise<LookupDictionaryItem | null>;
  update: (
    id: string,
    params: LookupDictionaryItemInput
  ) => Promise<LookupDictionaryItem | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // UI state management
  setSelectedLookupDictionaryItem: (
    lookupDictionaryItem: LookupDictionaryItem | null
  ) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "checkValue", direction: "asc" }];

export const useLookupDictionaryItems = (
  options: UseLookupDictionaryItemsOptions = {}
): UseLookupDictionaryItemsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = { status: [STATUSES.ACTIVE] },
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);
  const initialFiltersRef = useRef(initialFilters);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  // Data state
  const [lookupDictionaryItem, setLookupDictionaryItem] =
    useState<LookupDictionaryItem | null>(null);
  const [lookupDictionaryItems, setLookupDictionaryItems] = useState<
    LookupDictionaryItem[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<LookupDictionaryItemFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedLookupDictionaryItem, setSelectedLookupDictionaryItem] =
    useState<LookupDictionaryItem | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => lookupDictionaryItems.length === 0 && !loading,
    [lookupDictionaryItems.length, loading]
  );

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: LookupDictionaryItem) => {
    setLookupDictionaryItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setLookupDictionaryItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setLookupDictionaryItems((prev) =>
      prev.filter((item) => !idsSet.has(item.id || ""))
    );
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setLookupDictionaryItem(null);
    setLookupDictionaryItems([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setSelectedLookupDictionaryItem(null);
  }, []);

  // Get single lookup dictionary item (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await LookupDictionaryItemService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as LookupDictionaryItem[])[0];
          setLookupDictionaryItem(data);
          updateOrAddInList(data);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load lookup dictionary item";
          setLastError(errorMessage);
          setLookupDictionaryItem(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load lookup dictionary item";
        setLastError(errorMessage);
        setLookupDictionaryItem(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // List lookup dictionary items (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<LookupDictionaryItemFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      shouldReset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (shouldReset) {
          setLookupDictionaryItems([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await LookupDictionaryItemService.list(
          filter as LookupDictionaryItemFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as LookupDictionaryItem[])
            : [];

          if (isAppending) {
            setLookupDictionaryItems((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setLookupDictionaryItems(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load lookup dictionary items";
          setLastError(errorMessage);
          if (!isAppending) setLookupDictionaryItems([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load lookup dictionary items";
        setLastError(errorMessage);
        if (!isAppending) setLookupDictionaryItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many lookup dictionary items by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<LookupDictionaryItem[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await LookupDictionaryItemService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as LookupDictionaryItem[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return loadedItems;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load lookup dictionary items";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load lookup dictionary items";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // Create lookup dictionary item (matching API method name)
  const create = useCallback(
    async (
      params: LookupDictionaryItemInput
    ): Promise<LookupDictionaryItem | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await LookupDictionaryItemService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as LookupDictionaryItem[])[0];
          setLookupDictionaryItem(newItem);
          setLookupDictionaryItems((prev) => [newItem, ...prev]);
          setLastError(null);
          return newItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create lookup dictionary item";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create lookup dictionary item";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update lookup dictionary item (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: LookupDictionaryItemInput
    ): Promise<LookupDictionaryItem | null> => {
      if (!id) {
        setLastError("Lookup dictionary item ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await LookupDictionaryItemService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as LookupDictionaryItem[])[0];
          setLookupDictionaryItem(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update lookup dictionary item";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update lookup dictionary item";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove lookup dictionary item (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Lookup dictionary item ID is required for removal");
        return false;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await LookupDictionaryItemService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setLookupDictionaryItem(null);
          removeFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove lookup dictionary item";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove lookup dictionary item";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // Remove many lookup dictionary items (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError(
          "At least one lookup dictionary item ID is required for removal"
        );
        return false;
      }

      setLastError(null);

      try {
        const result = await LookupDictionaryItemService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setLookupDictionaryItem(null);
          removeManyFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove lookup dictionary items";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove lookup dictionary items";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeManyFromList]
  );

  // Load more lookup dictionary items
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh lookup dictionary items
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  return {
    lookupDictionaryItem,
    lookupDictionaryItems,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingId,
    selectedLookupDictionaryItem,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setSelectedLookupDictionaryItem,
    reset,
    lastError,
    clearError,
  };
};
